import { Message } from '../../../../../types';
import { cn } from '../../../../../lib/utils';
import { Markdown } from '@components/ui/markdown';
import { CopyButton } from '@components/ui';
import { copyToClipboard } from '@lib/content-utils';

interface ChatProps {
  messages: Message[];
  loading: boolean;
  isStreaming?: boolean;
}

export const Chat = ({ messages, loading, isStreaming = false }: ChatProps) => {
  const copyResponse = (msg: string) => {
    copyToClipboard(msg);
  };

  if (!messages.length && !loading) {
    return null;
  }

  return (
    <div className="flex w-full flex-col space-y-4 px-3">
      {messages.map((m) => {
        const isUser = m.role === 'user';

        return (
          <div key={m.id} className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
            <div className="max-w-[85%] flex-1 sm:max-w-[75%]">
              {isUser ? (
                <div className="flex justify-end">
                  <div className="rounded-lg bg-blue-500 px-4 py-2 text-white">{m.content}</div>
                </div>
              ) : (
                <div>
                  <div className="prose prose-sm max-w-none rounded-lg border bg-gray-100 p-3">
                    <Markdown>{m.content}</Markdown>
                    {/* Show streaming cursor if this is the last message and streaming */}
                    {isStreaming && messages[messages.length - 1]?.id === m.id && (
                      <span className="inline-flex items-center ml-1 animate-pulse">
                        <span className="w-0.5 h-4 bg-blue-500 rounded-full opacity-75"></span>
                      </span>
                    )}
                  </div>
                  <div className="mt-2">
                    <div className="flex gap-2">
                      <CopyButton onCopy={() => copyResponse(m.content)} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Show thinking animation only when loading but not streaming (i.e., before stream starts) */}
      {loading &&
        !isStreaming &&
        messages.length > 0 &&
        messages[messages.length - 1]?.role === 'user' && (
          <div className="flex justify-start">
            <div className="max-w-[85%] flex-1 sm:max-w-[75%]">
              <div className="prose prose-sm max-w-none rounded-lg border bg-gray-100 p-3">
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div
                      className="h-1.5 w-1.5 animate-bounce rounded-full bg-blue-500"
                      style={{ animationDelay: '0ms' }}
                    ></div>
                    <div
                      className="h-1.5 w-1.5 animate-bounce rounded-full bg-blue-500"
                      style={{ animationDelay: '150ms' }}
                    ></div>
                    <div
                      className="h-1.5 w-1.5 animate-bounce rounded-full bg-blue-500"
                      style={{ animationDelay: '300ms' }}
                    ></div>
                  </div>
                  <span className="text-xs font-medium text-gray-500">AI is thinking...</span>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};
